// A storefront save that loses a race with the AI assistant's edit lease.
//
// The editors poll the lease and go read-only while it is held, but polling is not a lock: the
// assistant can take the lease in the gap between two polls, or in the gap between the user pressing
// Save and the request landing. When that happens the API answers 409 instead of writing, and the
// user deserves to be told that their changes are still there and worth retrying, rather than a
// generic "failed to save" that reads like data loss.

export const STOREFRONT_LEASE_CONFLICT_MESSAGE =
    'Your changes were not saved because Adaptive Intelligence is applying changes to this storefront. Try again in a moment.';

/**
 * True when a rejected RTK Query mutation was refused by the edit lease.
 *
 * Matches on the 409 alone. That status is not used anywhere else on the storefront write endpoints,
 * and matching on the message text instead would break the moment the server wording is edited.
 */
export const isStorefrontLeaseConflict = (error: unknown): boolean => {
    if (typeof error !== 'object' || error === null) return false;
    const status = (error as { status?: unknown }).status;
    // fetchBaseQuery reports HTTP failures as a numeric `status`; the string forms ('FETCH_ERROR',
    // 'PARSING_ERROR', …) are transport problems, never a lease refusal.
    return status === 409;
};

/**
 * The message to show for a failed storefront save: the friendly lease explanation when the lease
 * refused it, and the caller's own wording for everything else.
 */
export const storefrontSaveErrorMessage = (error: unknown, fallback: string): string =>
    isStorefrontLeaseConflict(error) ? STOREFRONT_LEASE_CONFLICT_MESSAGE : fallback;
